import React, { Fragment } from 'react';
import { createSelector } from 'reselect';
import Button from '@material-ui/core/Button';
import ButtonBase from '@material-ui/core/ButtonBase';
import Card from '@material-ui/core/Card';
import CardActions from '@material-ui/core/CardActions';
import CardMedia from '@material-ui/core/CardMedia';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormLabel from '@material-ui/core/FormLabel';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { withStyles } from '@material-ui/core/styles';
import ClearIcon from '@material-ui/icons/Clear';
import ReactSelect from 'components/ReactSelect';
import ListDialog from 'components/ListDialog';
import QuillEditor from 'components/QuillEditor';

const blobUrls = new WeakMap();

const blobUrl = blob => {
  if (blobUrls.has(blob)) {
    return blobUrls.get(blob);
  } else {
    let url = URL.createObjectURL(blob);
    blobUrls.set(blob, url);
    return url;
  }
};

const dialogContent = {
  storyTips: {
    dialogTitle: 'Tips for Stories',
    dialogItems: [
      'Do you have an interesting video to share about an experience or an eatery? Upload it here and tell us about it.',
      'Create a visual story using images and some descriptions. Images can sometimes talk much louder than words.',
      'Narrate a story in detail. Share your experience and describe it using all your senses - what did you see, hear, smell, touch and feel? Alternate text with images or videos to make a compelling narrative.',
    ],
  },
  writingGuidelines: {
    dialogTitle: 'Writing Guidelines',
    dialogItems: [
      'What makes an eatery irresistible to us is the story. Every place has a beginning that started as an idea, that shows us how the owners envisioned, how they come about creating this lovely food. Spin us that tale and you’ll have our attention always.',
      'What does the place look like? Is the small, big, rustic, posh, charming, themed? Paint us a picture with your words, or with an actual picture.',
      'Does the ambiance set the mood? Some light music, live music, dim light, bright light, flashy lights? Tell us what we should expect.',
      'Let’s not forget the food! How does it look? Does it make you drool? In what did they serve it? What was that first bite like? Did the flavours explode in your mouth? Let us live vicariously through that adventure of yours.',
      'Did the food blow your mind away? Tell us more, what about it was outstanding? What did you taste? Sweet, sour, crunchy, umami? Describe it for us.',
    ],
  },
};

const styles = theme => ({
  cardMedia: {
    height: 108,
  }
});

class Editor extends React.Component {
  state = {
    dialogOpen: false,
    dialogTitle: '',
    dialogItems: [],
  };

  formats = [
    'header',
    'bold',
    'italic',
    'blockquote',
    'list',
    'bullet',
    'link',
    'image',
    'video',
  ];

  modules = {
    toolbar: {
      container: '#ql-toolbar',
    },
  };

  handleFeatureImageChange = e => {
    this.props.setFieldValue('featuredImage.file', e.currentTarget.files[0]);
  };

  handleContentChange = (content, delta, source, editor) => {
    this.props.setFieldValue('content', content);
  };

  handleContentBlur = () => {
    this.props.setFieldTouched('content');
  };

  handleDialogButtonClick = ({ dialogTitle, dialogItems }) => {
    this.setState({ dialogTitle, dialogItems, dialogOpen: true });
  };

  handleDialogClose = () => {
    this.setState({ dialogOpen: false });
  };

  selectEateries = props => props.derived.eateries;

  selectCities = props => props.values.cities;

  selectCityIds = createSelector(
    this.selectCities,
    cities => cities.map(({ value }) => value)
  );

  selectEateriesInCities = createSelector(
    this.selectEateries,
    this.selectCityIds,
    (eateries, cityIds) => eateries.filter(({ city }) => cityIds.includes(city))
  );

  render() {
    const {
      classes,
      errors,
      handleChange,
      handleBlur,
      setFieldValue,
      touched,
      values,
      derived: { cities, cuisines, dishTags },
    } = this.props;
    return (
      <Fragment>
        <Typography
          align="center"
          color="textPrimary"
          component="h2"
          gutterBottom
          variant="h4"
        >
          {values.id ? ' Edit story' : 'Add story'}
        </Typography>

        <TextField
          autoComplete="off"
          fullWidth
          helperText="The best description for the post coming below."
          inputProps={{ maxLength: 70 }}
          label="Title"
          margin="dense"
          name="title"
          onBlur={handleBlur}
          onChange={handleChange}
          required
          value={values.title}
        />

        <FormControl
          className="w-full"
          error={!!errors.featuredImage}
          margin="normal"
          required
        >
          <FormLabel className="mb-2" component="legend">
            Featured Image
          </FormLabel>
          <FormHelperText className="mb-2">
            A visual representation for your story.
          </FormHelperText>
          {(values.featuredImage.file || values.featuredImage.uri) && (
            <Card>
              <CardMedia
                className={classes.cardMedia}
                image={values.featuredImage.uri ? values.featuredImage.uri : blobUrl(values.featuredImage.file)}
              />
              <CardActions className="flex-col items-stretch">
                <TextField
                  className="my-1"
                  autoComplete="name"
                  inputProps={{ maxLength: 60 }}
                  label="Photo credit"
                  name="featuredImage.photoCredit"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  required
                  spellCheck="false"
                  value={values.featuredImage.photoCredit ? values.featuredImage.photoCredit : ""}
                />

                <div className="flex justify-end mt-1">
                  <IconButton
                    className="p-1"
                    aria-label="Remove"
                    onClick={() => {
                      setFieldValue('featuredImage.file', null);
                    }}
                  >
                    <ClearIcon />
                  </IconButton>
                </div>
              </CardActions>
            </Card>
          )}
          <div className="mt-4">
            <input
              accept=".jpg,.jpeg,image/jpeg"
              className="hidden"
              id="featured-file"
              name="values.featuredImage.file"
              onChange={this.handleFeatureImageChange}
              type="file"
            />
            <label htmlFor="featured-file">
              <Button variant="contained" component="span">
                Attach image
              </Button>
            </label>
          </div>
        </FormControl>

        <TextField
          autoComplete="off"
          fullWidth
          helperText="The post's highlight, expressed in one line."
          inputProps={{ maxLength: 70 }}
          label="Summary"
          margin="dense"
          name="summary"
          onBlur={handleBlur}
          onChange={handleChange}
          placeholder=""
          required
          value={values.summary ? values.summary : ""}
        />

        <ReactSelect
          className="w-full pt-4"
          error={touched.cities && !!errors.cities}
          formik={{ handleBlur, setFieldValue }}
          margin="normal"
          helperText="Pick the cities the post is set in."
          label="Cities"
          required
          selectProps={{
            name: 'cities',
            options: cities,
            value: values.cities,
          }}
        />

        <TextField
          autoComplete="address-level3"
          fullWidth
          helperText="Optional, but sometimes helps to know where."
          inputProps={{ maxLength: 70 }}
          label="Locality"
          margin="dense"
          name="locality"
          onBlur={handleBlur}
          onChange={handleChange}
          value={values.locality}
        />

        <ReactSelect
          className="w-full pt-4"
          error={touched.cuisines && !!errors.cuisines}
          formik={{ handleBlur, setFieldValue }}
          helperText="On a foodie platform, each post is about one or more cuisines."
          label="Cuisines"
          margin="normal"
          required
          selectProps={{
            isMulti: true,
            name: 'cuisines',
            options: cuisines,
            value: values.cuisines,
          }}
        />

        <ReactSelect
          className="w-full pt-4"
          createable
          formik={{ handleBlur, setFieldValue }}
          helperText="Get more specific, if your post calls for it."
          label="Dishes"
          margin="normal"
          selectProps={{
            isMulti: true,
            name: 'dishes',
            options: dishTags,
            value: values.dishes,
          }}
        />

        <ReactSelect
          className="w-full pt-4"
          error={touched.eateries && !!errors.eateries}
          formik={{ handleBlur, setFieldValue }}
          helperText="Are you sharing something about a particular eatery? A story, images or video(s)?"
          label="Eatery(s)"
          margin="normal"
          selectProps={{
            isDisabled: values.cities.length === 0,
            isMulti: true,
            name: 'eateries',
            options: this.selectEateriesInCities(this.props),
            value: values.eateries,
          }}
        />

        <FormControl
          className="w-full"
          error={touched.content && !!errors.content}
          margin="normal"
          required
        >
          <FormLabel className="mb-2" component="legend">
            Your Post
          </FormLabel>
          <FormHelperText>
            Use the editor below to craft your post. Upload images or videos and
            write your story. Arrange your content creatively.&nbsp;
            <ButtonBase
              className={classes.helperTextButton}
              color="primary"
              component="span"
              onClick={() =>
                this.handleDialogButtonClick(dialogContent.storyTips)
              }
              size="small"
            >
              Click here
            </ButtonBase>
            &nbsp;for more tips or see our&nbsp;
            <ButtonBase
              className={classes.helperTextButton}
              color="primary"
              component="span"
              onClick={() =>
                this.handleDialogButtonClick(dialogContent.writingGuidelines)
              }
              size="small"
            >
              writing guidelines
            </ButtonBase>
            .
          </FormHelperText>
          <QuillEditor
            value={values.content}
            handleContentChange={this.handleContentChange}
            handleContentBlur={this.handleContentBlur}
          />
        </FormControl>

        {this.state.dialogOpen && (
          <ListDialog
            open={this.state.dialogOpen}
            onClose={this.handleDialogClose}
            title={this.state.dialogTitle}
            items={this.state.dialogItems}
          />
        )}
      </Fragment>
    );
  }
}

export default withStyles(styles)(Editor);
